import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from './api.ts';
import type { Breadcrumb, DriveFile, Folder, Page } from './types.ts';
import { sheetKeys, type SpreadsheetSummary } from './sheet-queries.ts';
import type { DocumentSummary } from './document-queries.ts';

/**
 * Query keys are structured so a folder's contents can be invalidated
 * precisely. Invalidating everything on each mutation would work and would
 * also refetch every folder the user has visited — fine at ten files,
 * wasteful at ten thousand.
 */
export const keys = {
  folders: (parentId: string | null) => ['folders', parentId] as const,
  files: (folderId: string | null) => ['files', folderId] as const,
  breadcrumbs: (id: string) => ['breadcrumbs', id] as const,
  search: (term: string) => ['search', term] as const,
  access: (id: string) => ['access', id] as const,
};

/** How the server should handle an upload whose name is already taken. */
export type ConflictPolicy = 'error' | 'rename' | 'replace';

export interface NameCheck {
  taken: boolean;
  /** A free name in the "(1)" series — what "Keep both" would use. */
  suggestion: string;
  /** What the thing being placed will land as, which changes the wording. */
  kind: 'sheet' | 'file' | 'document';
}

/**
 * Ask whether a name is free before sending the bytes.
 *
 * Answering the conflict question up front means a large file is never
 * uploaded twice just to find out its name was taken, and nothing has to be
 * undone if the answer is "don't".
 */
export function checkName(
  folderId: string | null,
  name: string,
  /**
   * What the name is for, where the caller knows.
   *
   * An upload does not — its filename decides whether it lands as a file or
   * becomes a sheet, and the server works that out. The New menu does know,
   * and saying so matters: a new document collides with documents, and
   * without this the check ran against files, always answered "free", and
   * left the create to fail with a refusal the user could do nothing with.
   */
  kind?: 'document' | 'sheet',
): Promise<NameCheck> {
  const params = new URLSearchParams({ name });
  if (folderId) params.set('folderId', folderId);
  if (kind) params.set('kind', kind);
  return api.get<NameCheck>(`/api/drive/name-check?${params.toString()}`);
}

/** What this account may do in a folder. Null means it cannot see it at all. */
export type AccessLevel = 'VIEW' | 'EDIT' | 'MANAGE';

/**
 * What the signed-in account may do where it currently is.
 *
 * Asked of the server rather than worked out here. The Drive is closed —
 * a folder nobody was granted is invisible — and which folders those are is
 * the server's to know; a copy of the rule in the browser would be a second
 * opinion that eventually disagrees.
 */
export function useAccess(folderId: string | null) {
  return useQuery({
    queryKey: keys.access(folderId ?? 'root'),
    queryFn: () =>
      api.get<{ level: AccessLevel | null }>(
        `/api/drive/access${folderId ? `?folderId=${folderId}` : ''}`,
      ),
    staleTime: 60_000,
  });
}

export function useFolders(parentId: string | null) {
  return useQuery({
    queryKey: keys.folders(parentId),
    queryFn: () =>
      api.get<Folder[]>(`/api/drive/folders${parentId ? `?parentId=${parentId}` : ''}`),
  });
}

export function useFiles(folderId: string | null) {
  return useQuery({
    queryKey: keys.files(folderId),
    queryFn: () =>
      api.get<Page<DriveFile>>(`/api/drive/files${folderId ? `?folderId=${folderId}` : ''}`),
  });
}

export function useBreadcrumbs(folderId: string | null) {
  return useQuery({
    queryKey: keys.breadcrumbs(folderId ?? 'root'),
    queryFn: () => api.get<Breadcrumb[]>(`/api/drive/folders/${folderId}/breadcrumbs`),
    enabled: folderId !== null,
  });
}

export interface DriveSearchResults {
  folders: Folder[];
  files: DriveFile[];
  spreadsheets: SpreadsheetSummary[];
  documents: DocumentSummary[];
}

export function useSearch(term: string) {
  return useQuery({
    queryKey: keys.search(term),
    // Everything the Drive shows, not just uploaded files: a workbook becomes
    // a *sheet* on upload, so a files-only search could never find the thing
    // the client had just put in.
    queryFn: () => api.get<DriveSearchResults>(`/api/drive/search?q=${encodeURIComponent(term)}`),
    // Below two characters the server returns nothing, so do not ask.
    enabled: term.trim().length >= 2,
  });
}

/** Both lists in the current folder — what almost every mutation invalidates. */
function invalidateFolder(client: QueryClient, folderId: string | null): Promise<void[]> {
  return Promise.all([
    client.invalidateQueries({ queryKey: keys.folders(folderId) }),
    client.invalidateQueries({ queryKey: keys.files(folderId) }),
  ]) as unknown as Promise<void[]>;
}

/**
 * Everything a finished upload could have changed.
 *
 * The sheet list as well as the file list: a workbook is converted on arrival
 * and the upload itself moves to the trash, so refreshing only the files left
 * the upload gone from one list and the sheet absent from the other — the row
 * vanished when the spinner stopped and did not come back until a reload.
 *
 * Exported because the upload queue lives outside any one page now and has to
 * refresh whatever folder the file actually landed in, which may not be the
 * folder on screen.
 */
export function invalidateAfterUpload(
  client: QueryClient,
  folderId: string | null,
): Promise<unknown> {
  return Promise.all([
    invalidateFolder(client, folderId),
    /**
     * Every folder listing, not only the one the file landed in.
     *
     * Uploading a folder creates folders as it goes, and they belong to
     * whichever listing is on screen rather than to the target — which is
     * itself one of the new ones. Invalidating only the target left the
     * subfolders it had just made invisible until the page was reloaded,
     * which is exactly what the upload was for.
     *
     * The whole prefix is cheap: these listings are small and a refetch of
     * one that has not changed costs a request nobody notices.
     */
    client.invalidateQueries({ queryKey: ['folders'] }),
    client.invalidateQueries({ queryKey: ['files'] }),
    client.invalidateQueries({ queryKey: sheetKeys.all }),
    client.invalidateQueries({ queryKey: ['documents'] }),
    client.invalidateQueries({ queryKey: ['dashboard'] }),
  ]);
}

export function useCreateFolder(parentId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post<Folder>('/api/drive/folders', { name, parentId }),
    onSuccess: () => invalidateFolder(client, parentId),
  });
}

export function useRenameFolder(parentId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.post<Folder>(`/api/drive/folders/${id}/rename`, { name }),
    onSuccess: () => invalidateFolder(client, parentId),
  });
}

export function useTrashFolder(parentId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/drive/folders/${id}`),
    onSuccess: () => invalidateFolder(client, parentId),
  });
}

/**
 * Move something into a folder, as dragging it there does.
 *
 * Both lists are invalidated, not just the one being left: the item has to
 * disappear from here and appear there, and refreshing only the source folder
 * left a stale copy in the destination the moment anyone navigated into it.
 */
export function useMoveFile(folderId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, targetId }: { id: string; targetId: string | null }) =>
      api.post<DriveFile>(`/api/drive/files/${id}/move`, { folderId: targetId }),
    onSuccess: (_result, variables) =>
      Promise.all([
        invalidateFolder(client, folderId),
        invalidateFolder(client, variables.targetId),
      ]),
  });
}

export function useMoveFolder(parentId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, targetId }: { id: string; targetId: string | null }) =>
      api.post<Folder>(`/api/drive/folders/${id}/move`, { parentId: targetId }),
    onSuccess: (_result, variables) =>
      Promise.all([
        invalidateFolder(client, parentId),
        invalidateFolder(client, variables.targetId),
      ]),
  });
}

export function useRenameFile(folderId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.post<DriveFile>(`/api/drive/files/${id}/rename`, { name }),
    onSuccess: () => invalidateFolder(client, folderId),
  });
}

export function useTrashFile(folderId: string | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/drive/files/${id}`),
    onSuccess: () => invalidateFolder(client, folderId),
  });
}

export interface UploadProgress {
  filename: string;
  status: 'uploading' | 'done' | 'error';
  message?: string;
}
